import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import type { UserFavourite } from '@domain/models/user-favourite'
import { UserFavouriteConverter } from '@core/services/user-favourites/converters/user-favourite-converter'
import { UserFavouriteErrorConverter } from '@core/services/user-favourites/converters/user-favourite-error-converter'

const USER_FAVOURITES_COLLECTION = 'user_favourites'

function getUserFavouritesCollection() {
  if (!firestore) {
    throw UserFavouriteErrorConverter.domainModel(
      new Error('Firestore is not initialized'),
    )
  }
  return collection(firestore, USER_FAVOURITES_COLLECTION)
}

function buildUserFavouritesQuery(
  userId: string,
  type?: string,
  itemId?: string,
) {
  const constraints: QueryConstraint[] = [where('user_id', '==', userId)]

  if (type) {
    constraints.push(where('type', '==', type))
  }
  if (itemId) {
    constraints.push(where('item_id', '==', itemId))
  }

  constraints.push(orderBy('created_time', 'desc'))
  return query(getUserFavouritesCollection(), ...constraints)
}

export interface CreateUserFavouriteParams {
  userId: string
  itemId: string
  type: string
}

export class UserFavouritesService {
  private static instance: UserFavouritesService

  private constructor() {}

  public static getInstance(): UserFavouritesService {
    if (!UserFavouritesService.instance) {
      UserFavouritesService.instance = new UserFavouritesService()
    }
    return UserFavouritesService.instance
  }

  public async createFavourite(params: CreateUserFavouriteParams): Promise<string> {
    try {
      const data = UserFavouriteConverter.toFirestore({
        userId: params.userId,
        itemId: params.itemId,
        type: params.type,
      })
      const docRef = await addDoc(getUserFavouritesCollection(), data)
      return docRef.id
    } catch (error) {
      throw UserFavouriteErrorConverter.domainModel(error)
    }
  }

  public async deleteFavourite(documentId: string): Promise<void> {
    try {
      await deleteDoc(doc(getUserFavouritesCollection(), documentId))
    } catch (error) {
      throw UserFavouriteErrorConverter.domainModel(error)
    }
  }

  public async fetchFavourites(userId: string, type?: string): Promise<UserFavourite[]> {
    try {
      const snapshot = await getDocs(buildUserFavouritesQuery(userId, type))
      return snapshot.docs.map((item) =>
        UserFavouriteConverter.fromFirestore(item.id, item.data()),
      )
    } catch (error) {
      throw UserFavouriteErrorConverter.domainModel(error)
    }
  }

  public async fetchFavourite(
    userId: string,
    itemId: string,
    type?: string,
  ): Promise<UserFavourite | null> {
    try {
      const snapshot = await getDocs(
        query(buildUserFavouritesQuery(userId, type, itemId), limit(1)),
      )
      if (snapshot.docs.length === 0) {
        return null
      }
      const firstDoc = snapshot.docs[0]
      return UserFavouriteConverter.fromFirestore(firstDoc.id, firstDoc.data())
    } catch (error) {
      throw UserFavouriteErrorConverter.domainModel(error)
    }
  }

  public observeFavourites(
    userId: string,
    callback: (favourites: UserFavourite[]) => void,
    onError?: (error: Error) => void,
    type?: string,
  ): Unsubscribe {
    return onSnapshot(
      buildUserFavouritesQuery(userId, type),
      (snapshot) => {
        callback(snapshot.docs.map((item) =>
          UserFavouriteConverter.fromFirestore(item.id, item.data())
        ))
      },
      (error) => {
        onError?.(UserFavouriteErrorConverter.domainModel(error))
      },
    )
  }

  public observeFavourite(
    userId: string,
    itemId: string,
    callback: (favourite: UserFavourite | null) => void,
    onError?: (error: Error) => void,
    type?: string,
  ): Unsubscribe {
    return onSnapshot(
      query(buildUserFavouritesQuery(userId, type, itemId), limit(1)),
      (snapshot) => {
        if (snapshot.docs.length === 0) {
          callback(null)
          return
        }
        const firstDoc = snapshot.docs[0]
        callback(UserFavouriteConverter.fromFirestore(firstDoc.id, firstDoc.data()))
      },
      (error) => {
        onError?.(UserFavouriteErrorConverter.domainModel(error))
      },
    )
  }
}

const userFavouritesService = UserFavouritesService.getInstance()

export async function createFavourite(
  params: CreateUserFavouriteParams,
): Promise<string> {
  return userFavouritesService.createFavourite(params)
}

export async function deleteFavourite(documentId: string): Promise<void> {
  return userFavouritesService.deleteFavourite(documentId)
}

export async function fetchFavourites(
  userId: string,
  type?: string,
): Promise<UserFavourite[]> {
  return userFavouritesService.fetchFavourites(userId, type)
}

export async function fetchFavourite(
  userId: string,
  itemId: string,
  type?: string,
): Promise<UserFavourite | null> {
  return userFavouritesService.fetchFavourite(userId, itemId, type)
}

export function observeFavourites(
  userId: string,
  callback: (favourites: UserFavourite[]) => void,
  onError?: (error: Error) => void,
  type?: string,
): Unsubscribe {
  return userFavouritesService.observeFavourites(userId, callback, onError, type)
}

export function observeFavourite(
  userId: string,
  itemId: string,
  callback: (favourite: UserFavourite | null) => void,
  onError?: (error: Error) => void,
  type?: string,
): Unsubscribe {
  return userFavouritesService.observeFavourite(
    userId,
    itemId,
    callback,
    onError,
    type,
  )
}
